import {
  Button,
  Divider,
  Flex,
  HStack,
  Heading,
  Icon,
  Text,
  VStack,
  useDisclosure,
} from '@chakra-ui/react';

import Content from '~/containers/Content';

import { CgSmartHomeCooker } from 'react-icons/cg';
import {
  IoCalendarOutline,
  IoCreateOutline,
  IoMailSharp,
  IoMaleFemaleOutline,
  IoPhonePortraitOutline,
} from 'react-icons/io5';
import { MdOutlineRiceBowl } from 'react-icons/md';

import { ReactNode } from 'react';
import { IconType } from 'react-icons';
import { IoIosHeart } from 'react-icons/io';
import { MdBarChart } from 'react-icons/md';
import CustomerInfoEditDialog from '~/components/CustomerInfoEditDialog';
import { BtnVariant } from '~/theme/components/Button';
import {
  AGE_LEVEL_STRING,
  CHILLI_TASTE_STRING,
  COOKING_STYLE_STRING,
  Customer,
  GENDER_STRING,
  TZUCHI_EATING_STRING,
  TZUCHI_SOUR_STRING,
  TZUCHI_TASTE_STRING,
} from '~/types/platform/customer';

const INFOS: Array<{
  key: keyof Customer;
  label: string;
  icon: IconType;
  onFormat?: (data: Customer) => ReactNode;
}> = [
  { key: 'phoneNumber', label: '手機', icon: IoPhonePortraitOutline },
  {
    key: 'gender',
    label: '性別',
    icon: IoMaleFemaleOutline,
    onFormat: (data) => GENDER_STRING[data.gender],
  },
  {
    key: 'age',
    label: '年齡區間',
    icon: MdBarChart,
    onFormat: (data) => AGE_LEVEL_STRING[data.age],
  },
  { key: 'email', label: '電子信箱', icon: IoMailSharp },
  {
    key: 'birthday',
    label: '出生日期',
    icon: IoCalendarOutline,
    onFormat: (data) => (data.birthday ? data.birthday?.replaceAll('-', '/') : '-'),
  },
  {
    key: 'cookingStyle',
    label: '喜歡的料理方式',
    icon: CgSmartHomeCooker,
    onFormat: (data) =>
      (data.cookingStyle || [])?.length > 0
        ? (data.cookingStyle || []).map((value) => COOKING_STYLE_STRING[value]).join('、')
        : '-',
  },
  {
    key: 'taste',
    label: '口味偏好',
    icon: IoIosHeart,
    onFormat: (data) => (
      <>
        {data?.chilliTaste ? CHILLI_TASTE_STRING[data?.chilliTaste] : '-'}/
        {data?.sourTaste ? TZUCHI_SOUR_STRING[data?.sourTaste] : '-'}/
        {data?.taste ? TZUCHI_TASTE_STRING[data?.taste] : '-'}
      </>
    ),
  },
  {
    key: 'eatingStyle',
    label: '飲食風格',
    icon: MdOutlineRiceBowl,
    onFormat: (data) =>
      (data.eatingStyle || [])?.length > 0
        ? (data.eatingStyle || []).map((value) => TZUCHI_EATING_STRING[value]).join('、')
        : '-',
  },
];

export default function MemberCenter({ customer }: { customer: Customer }) {
  const { isOpen, onClose, onOpen } = useDisclosure();

  return (
    <>
      <Content>
        <VStack
          p={3}
          spacing={3}
          align="stretch"
          borderWidth="1px"
          borderColor="primaryLight"
          borderRadius="5px"
        >
          <Heading size="sm">{customer.displayName}</Heading>
          <Divider borderColor="primaryLight" />

          {INFOS.map((info) => {
            return (
              <Flex key={info.key} justify="space-between">
                <HStack align="start">
                  <Icon boxSize="1rem" as={info?.icon} color="#333" />
                  <Text fontSize="14px" fontWeight={700} color="#333" whiteSpace="nowrap">
                    {info.label}
                  </Text>
                </HStack>
                <Text fontSize="14px" fontWeight={400} color="#333" ml="15" textAlign="right">
                  {info?.onFormat ? info?.onFormat(customer) : customer?.[info.key] || '-'}
                </Text>
              </Flex>
            );
          })}

          <Button
            variant={BtnVariant.SOLID_LIGHT}
            fontWeight="normal"
            rightIcon={<Icon as={IoCreateOutline} boxSize="1.25rem" />}
            onClick={onOpen}
          >
            編輯個人資訊
          </Button>
        </VStack>
      </Content>
      <CustomerInfoEditDialog isOpen={isOpen} onClose={onClose} />
    </>
  );
}
